import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReactPixel from 'react-facebook-pixel';
import { Order, Payment, ProductInterface } from '../services/exports/Interfaces';
import { CompleteBasketItem } from './BasketProvider';
import * as amplitude from '@amplitude/analytics-browser';
import { v4 as uuidv4 } from 'uuid';
import useLocalStorage, { LocalStorageReservedKey } from '../hooks/global/useLocalStorage';
import { ENVIRONMENTS } from '../services/exports/Constants';
import { CompiledProduct } from './MenuProvider';

export enum TrackableEvent {
  ViewItem = 'view_item',
  AddToBasket = 'add_to_basket',
  RemoveFromBasket = 'remove_from_basket',
  BeginCheckout = 'begin_checkout',
  BeginPurchase = 'begin_purchase',
  CompletePurchase = 'complete_purchase',

  // Google Analytics
  GA_Conversion = 'conversion',

  // Facebook Pixel
  FB_Purchase = 'Purchase',
}

type Props = {
  children: any;
};

export type AnalyticsContextType = {
  trackViewItem: (product: ProductInterface) => void;
  trackAddToBasket: (item: CompleteBasketItem) => void;
  trackRemoveFromBasket: (item: CompleteBasketItem) => void;
  trackBeginCheckout: (order: Order, lineItems: CompleteBasketItem[]) => void;
  trackBeginPurchase: (order: Order, lineItems: CompleteBasketItem[]) => void;
  trackCompletePurchase: (payment: Payment, order: Order) => void;
};

export const AnalyticsContext = React.createContext<AnalyticsContextType>(null);

export default function AnalyticsProvider({ children }: Props) {
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { company, franchise } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { getItem, setItem } = useLocalStorage();

  useEffect(() => {
    const userId = profile?.id?.toString()
      ? `user-${profile.id}`
      : (getItem(LocalStorageReservedKey.AnalyticsUserId) ?? uuidv4());

    setItem(LocalStorageReservedKey.AnalyticsUserId, userId);

    if (company?.service_provider?.amplitude_api_key) {
      amplitude.init(company?.service_provider?.amplitude_api_key, userId, {
        autocapture: false,
      });
    }
  }, [company?.service_provider?.amplitude_api_key, profile?.id]);

  useEffect(() => {
    window.gtag('config', franchise?.google_analytics_id ?? company?.service_provider?.google_analytics_id, {
      user_id: profile?.id,
      debug_mode: import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD,
    });
  }, [profile?.id, franchise?.google_analytics_id, company?.service_provider?.google_analytics_id]);

  const trackEvent = async (event: TrackableEvent, data?: object) => {
    window.gtag('event', event, data);
    ReactPixel.track(event, data);
    amplitude.track(event, data);
  };

  const trackViewItem = async (product: CompiledProduct) =>
    trackEvent(TrackableEvent.ViewItem, {
      company_id: company?.id,
      currency: company?.currency,
      value: product?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product?.actual_price,
          location_id: company?.google_places_id,
        },
      ],
    });

  const trackAddToBasket = async (item: CompleteBasketItem) =>
    trackEvent(TrackableEvent.AddToBasket, {
      company_id: company?.id,
      currency: company?.currency,
      value: item?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: item.product_id,
          item_name: item.entry?.name,
          price: item?.actual_price / item.quantity,
          quantity: item.quantity,
          location_id: company?.google_places_id,
        },
      ],
    });

  const trackRemoveFromBasket = async (item: CompleteBasketItem) =>
    trackEvent(TrackableEvent.RemoveFromBasket, {
      company_id: company?.id,
      currency: company?.currency,
      value: item?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: item.product_id,
          item_name: item.entry?.name,
          price: item?.actual_price / item.quantity,
          quantity: item.quantity,
          location_id: company?.google_places_id,
        },
      ],
    });

  const trackBeginCheckout = async (order: Order, lineItems: CompleteBasketItem[]) =>
    trackEvent(TrackableEvent.BeginCheckout, {
      company_id: company?.id,
      value: order.total,
      currency: company?.currency,
      order_method: order.method,
      delivery_mode: company?.delivery_mode,
      items: lineItems.map((item) => ({
        item_id: item.product_id,
        item_name: item.entry?.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: company?.google_places_id,
      })),
    });

  const trackBeginPurchase = async (order: Order, lineItems: CompleteBasketItem[]) =>
    trackEvent(TrackableEvent.BeginPurchase, {
      company_id: company?.id,
      value: order.total,
      currency: company?.currency,
      order_method: order.method,
      delivery_mode: company?.delivery_mode,
      items: lineItems.map((item) => ({
        item_id: item.product_id,
        item_name: item.entry?.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: company?.google_places_id,
      })),
    });

  const trackCompletePurchase = async (payment: Payment, order: Order) => {
    trackGoogleAnalyticsConversion(payment, order);
    trackFacebookPixelConversion(payment, order);
    trackEvent(TrackableEvent.CompletePurchase, {
      company_id: order.company?.id,
      transaction_id: payment.id,
      value: payment.amount,
      currency: order.company.currency,
      order_method: order.method,
      delivery_mode: company?.delivery_mode,
      items: order.line_items.map((item) => ({
        item_id: item.product_id,
        item_name: item.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: company?.google_places_id,
      })),
    });
  };

  const trackGoogleAnalyticsConversion = async (payment: Payment, order: Order) =>
    franchise?.google_analytics_id &&
    franchise?.google_ads_conversion_label_id &&
    window.gtag('event', TrackableEvent.GA_Conversion, {
      send_to: `${franchise.google_analytics_id}/${franchise.google_ads_conversion_label_id}`,
      value: payment.amount,
      currency: order.company?.currency,
      company_id: order.company?.id,
      transaction_id: payment.id,
      order_method: order.method,
      delivery_mode: order.company?.delivery_mode,
      items: order.line_items.map((item) => ({
        item_id: item.product_id,
        item_name: item.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: order.company?.google_places_id,
      })),
    });

  const trackFacebookPixelConversion = async (payment: Payment, order: Order) =>
    ReactPixel.track(TrackableEvent.FB_Purchase, { value: payment.amount, currency: order.company?.currency });

  const context = useMemo(
    () => ({
      trackViewItem,
      trackAddToBasket,
      trackRemoveFromBasket,
      trackBeginCheckout,
      trackBeginPurchase,
      trackCompletePurchase,
    }),
    [cached_order],
  );

  return <AnalyticsContext.Provider value={context}>{children}</AnalyticsContext.Provider>;
}
